import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo } from 'react';
const BlockedReasonContext = createContext(null);
const BlockedPressContext = createContext(null);
export function BlockedReasonProvider(props) {
    const { reason, onBlockedPress, children } = props;
    const parentPress = useContext(BlockedPressContext);
    const press = useMemo(() => {
        if (!onBlockedPress)
            return parentPress;
        if (!parentPress)
            return onBlockedPress;
        return (info) => {
            onBlockedPress(info);
            parentPress(info);
        };
    }, [onBlockedPress, parentPress]);
    // A provider that only listens leaves the inherited reason alone.
    const setsReason = Object.prototype.hasOwnProperty.call(props, 'reason');
    const inner = setsReason ? (_jsx(BlockedReasonContext.Provider, { value: reason ? reason : null, children: children })) : (children);
    return _jsx(BlockedPressContext.Provider, { value: press, children: inner });
}
/**
 * The reason a kit button is blocked: its own `disabledReason`, else the
 * nearest `BlockedReasonProvider`'s. `null` = not blocked.
 */
export function useBlockedReason(explicit) {
    const inherited = useContext(BlockedReasonContext);
    const reason = explicit ?? inherited;
    return reason ? reason : null;
}
/** The nearest `onBlockedPress` listener (composed with its ancestors), or `null`. */
export function useBlockedPress() {
    return useContext(BlockedPressContext);
}
/**
 * Report a swallowed press, if anyone listens. Never throws into the click.
 * A click, or — for a `DisabledReason` wrapper, which is a focusable span (or
 * div) and so never synthesises a click from a key — the Enter/Space keydown
 * itself.
 */
export function reportBlockedPress(handler, event, reason) {
    if (!handler)
        return;
    const native = event.nativeEvent;
    // `detail === 0` is a click synthesised by Enter/Space on a focused control.
    const input = 'key' in event || event.detail === 0 ? 'keyboard' : 'pointer';
    try {
        handler({
            element: event.currentTarget,
            reason,
            input,
            pointerType: input === 'pointer' ? (native?.pointerType ?? '') : '',
        });
    }
    catch {
        // A listener's bug must not turn a blocked press into a crash.
    }
}
/** The props that make a kit button "blocked". Used by `Button` and `IconButton`. */
export function blockedButtonProps(props, reason, prefixCls, onBlockedPress) {
    return {
        // `false`, not `undefined`: a blocked button inside a disabled Form must
        // still take focus, and AntD's DisabledContext would otherwise win.
        disabled: false,
        'aria-disabled': true,
        'aria-description': reason,
        // antd's own disabled look in both themes (its variant styles select
        // `&:disabled, &.ant-btn-disabled`; the wave skips `aria-disabled`), plus
        // our hook for the cursor.
        className: [props.className, 'bk-button--blocked', `${prefixCls}-disabled`]
            .filter(Boolean)
            .join(' '),
        // A blocked LinkButton must not navigate.
        href: undefined,
        // Also blocks an implicit form submit.
        onClick: (event) => {
            event.preventDefault();
            event.stopPropagation();
            reportBlockedPress(onBlockedPress, event, reason);
        },
    };
}
//# sourceMappingURL=blocked-reason.js.map